import { HttpContext, httpResource, HttpResourceRef } from '@angular/common/http';
import { computed, Service, Signal } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../../../core/http/http-context-tokens';
import { slugDeServico } from '../../../shared/utils/slug.util';
import {
  AberturaBloco,
  BlocoSecao,
  ChavePagina,
  CONTEUDO_PAGINAS,
  ContatoSecao,
  SecaoAtendimento,
} from '../pages-perguntas-politica-canais/conteudo-paginas';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/publico`;

/** O erro fica local: a página mostra a mensagem com "Tentar novamente", sem modal. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/** A rota da página de canais, base da página de cada bloco. */
export const ROTA_CANAIS = '/outros-canais-atendimento';

/** O ícone de quem não escolheu um no adm: o mesmo `ICONE_CONTEUDO_PADRAO` de lá. */
export const ICONE_PADRAO = 'circle-question-mark';

/** O tamanho do recorte da prévia do adm, que é o da imagem enviada. */
const IMAGEM_ENVIADA = { largura: 800, altura: 450 } as const;

/** A lista de cada página na API. */
const LISTA_API: Readonly<Record<ChavePagina, string>> = {
  canais: 'canal-atendimento',
  perguntas: 'pergunta-frequente',
  politica: 'politica-atendimento',
};

/** A chave de cada página na configuração da imagem (`tb_cse_pagina_portal`). */
export const CHAVE_API: Readonly<Record<ChavePagina, string>> = {
  canais: 'canais',
  perguntas: 'perguntas-frequentes',
  politica: 'politica-atendimento',
};

/** O comportamento gravado no adm, com o acento. Fora destes quatro, o bloco abre no painel. */
const ABERTURAS: Readonly<Record<string, AberturaBloco>> = {
  'Expandir Accordion': 'accordion',
  'Abrir Modal': 'modal',
  'Abrir Página Interna': 'pagina',
  'Abrir Nova Aba': 'nova-aba',
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SITE = /^https?:\/\//i;

const SITE_SEM_ESQUEMA = /^www\./i;

const SO_TELEFONE = /^[\d\s()+\-.]+$/;

const ACENTOS = /\p{Diacritic}/gu;

export interface BlocoPerguntaFrequentePublicoResponse {
  desTituloBpf: string;
  desConteudoBpf: string | null;
}

/** Uma pergunta de `GET publico/pergunta-frequente`: só `Publicada`, com os blocos habilitados. */
export interface PerguntaFrequentePublicaResponse {
  codPerguntaFrequentePfq: number;
  desPerguntaPfq: string;
  desResumoPfq: string;
  desIconePfq: string | null;
  blocos: BlocoPerguntaFrequentePublicoResponse[];
}

export interface BlocoPoliticaAtendimentoPublicoResponse {
  desTituloBpa: string;
  desConteudoBpa: string | null;
}

/** Uma política de `GET publico/politica-atendimento`: só `Publicada`, com os blocos habilitados. */
export interface PoliticaAtendimentoPublicaResponse {
  codPoliticaAtendimentoPta: number;
  desPoliticaPta: string;
  desResumoPta: string;
  desIconePta: string | null;
  blocos: BlocoPoliticaAtendimentoPublicoResponse[];
}

export interface BlocoCanalAtendimentoPublicoResponse {
  desTituloBca: string;
  desComportamentoBca: string;
  desConteudoBca: string | null;
}

/** Um canal de `GET publico/canal-atendimento`: só `Ativo`, por nome, com os blocos habilitados. */
export interface CanalAtendimentoPublicoResponse {
  codCanalAtendimentoCan: number;
  desCanalCan: string;
  desDescricaoCan: string;
  desIconeCan: string | null;
  desTipoCan: string;
  desContatoCan: string;
  blocos: BlocoCanalAtendimentoPublicoResponse[];
}

/** A imagem da página, de `GET publico/pagina/{chave}`. Versão nula é a ilustração padrão. */
export interface PaginaPublicaResponse {
  bitExibirImagemPgp: boolean;
  desVersaoImagemPgp: string | null;
}

type ItemPublico =
  | PerguntaFrequentePublicaResponse
  | PoliticaAtendimentoPublicaResponse
  | CanalAtendimentoPublicoResponse;

/** As seções de uma página, com os estados que o template desenha. */
export interface ConteudoAtendimento {
  readonly secoes: Signal<readonly SecaoAtendimento[]>;
  readonly carregando: Signal<boolean>;
  readonly erro: Signal<boolean>;
  readonly recarregar: () => void;
}

/**
 * A imagem ao lado da lista. `src` nulo com `oculta` falso é a configuração ainda
 * carregando: a coluna fica vazia, sem mostrar a ilustração padrão para trocá-la em
 * seguida pela enviada. `oculta` põe a página em uma coluna só.
 */
export interface ImagemAtendimento {
  src: string | null;
  largura: number;
  altura: number;
  oculta: boolean;
}

function normalizar(valor: string): string {
  return valor.normalize('NFD').replace(ACENTOS, '').toLowerCase().trim();
}

/**
 * O texto de um HTML do adm, para a busca. O `DOMParser` tira as tags e também decodifica
 * as entidades que o TinyMCE grava (`n&atilde;o` vira "não"); uma expressão regular só
 * tiraria as tags. O documento analisado não executa script nem carrega imagem.
 */
export function textoDoHtml(html: string | null | undefined): string {
  if (!html) return '';

  const documento = new DOMParser().parseFromString(html, 'text/html');

  return (documento.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * O contato do canal pela FORMA do valor, porque nem o tipo nem o contato são validados no
 * adm: e-mail, site (com ou sem `http`) e telefone viram link; o resto, texto. O telefone
 * só vale no tipo "Telefone", para um endereço com número não virar `tel:`.
 */
export function contatoDoCanal(tipo: string | null, valor: string | null): ContatoSecao | null {
  const texto = valor?.trim() ?? '';
  if (!texto) return null;

  const rotulo = tipo?.trim() || 'Contato';
  const contato = (href: string | null, externo = false): ContatoSecao => ({
    rotulo,
    valor: texto,
    href,
    externo,
  });

  if (EMAIL.test(texto)) return contato(`mailto:${texto}`);
  if (SITE.test(texto)) return contato(texto, true);
  if (SITE_SEM_ESQUEMA.test(texto)) return contato(`https://${texto}`, true);

  const digitos = texto.replace(/\D/g, '');
  if (normalizar(rotulo) === 'telefone' && SO_TELEFONE.test(texto) && digitos.length >= 3) {
    return contato(`tel:${digitos}`);
  }

  return contato(null);
}

export function aberturaDoComportamento(comportamento: string | null | undefined): AberturaBloco {
  return ABERTURAS[comportamento ?? ''] ?? 'accordion';
}

/**
 * A rota da página de um bloco de canal: o slug do nome do canal e o do título do bloco,
 * como serviço e categoria. O código do bloco não serve: o adm troca todos a cada gravação.
 */
export function rotaDoBloco(canal: string, bloco: string): string[] | null {
  const slugCanal = slugDeServico(canal);
  const slugBloco = slugDeServico(bloco);

  return slugCanal && slugBloco ? [ROTA_CANAIS, slugCanal, slugBloco] : null;
}

function iconeOuPadrao(icone: string | null | undefined): string {
  return icone?.trim() || ICONE_PADRAO;
}

/**
 * Um bloco pronto para o painel, ou nulo quando não sobra texto depois de tirar as tags (o
 * adm já o impede; aqui é defesa). A página interna e a nova aba precisam de rota: sem ela,
 * o bloco abre no painel.
 */
function blocoSecao(
  titulo: string,
  html: string | null,
  abertura: AberturaBloco,
  canal: string | null
): BlocoSecao | null {
  const texto = textoDoHtml(html);
  if (!texto) return null;

  const pedeRota = abertura === 'pagina' || abertura === 'nova-aba';
  const rota = pedeRota && canal !== null ? rotaDoBloco(canal, titulo) : null;

  return {
    titulo,
    html: html ?? '',
    texto,
    abertura: pedeRota && !rota ? 'accordion' : abertura,
    rota,
  };
}

function semNulos<T>(itens: readonly (T | null)[]): T[] {
  return itens.filter((item): item is T => item !== null);
}

export function secoesDePerguntas(
  perguntas: readonly PerguntaFrequentePublicaResponse[]
): SecaoAtendimento[] {
  return perguntas.map((pergunta) => ({
    id: `pergunta-${pergunta.codPerguntaFrequentePfq}`,
    titulo: pergunta.desPerguntaPfq,
    icone: iconeOuPadrao(pergunta.desIconePfq),
    descricao: pergunta.desResumoPfq?.trim() ?? '',
    contato: null,
    blocos: semNulos(
      (pergunta.blocos ?? []).map((bloco) =>
        blocoSecao(bloco.desTituloBpf, bloco.desConteudoBpf, 'accordion', null)
      )
    ),
  }));
}

export function secoesDePoliticas(
  politicas: readonly PoliticaAtendimentoPublicaResponse[]
): SecaoAtendimento[] {
  return politicas.map((politica) => ({
    id: `politica-${politica.codPoliticaAtendimentoPta}`,
    titulo: politica.desPoliticaPta,
    icone: iconeOuPadrao(politica.desIconePta),
    descricao: politica.desResumoPta?.trim() ?? '',
    contato: null,
    blocos: semNulos(
      (politica.blocos ?? []).map((bloco) =>
        blocoSecao(bloco.desTituloBpa, bloco.desConteudoBpa, 'accordion', null)
      )
    ),
  }));
}

export function secoesDeCanais(canais: readonly CanalAtendimentoPublicoResponse[]): SecaoAtendimento[] {
  return canais.map((canal) => ({
    id: `canal-${canal.codCanalAtendimentoCan}`,
    titulo: canal.desCanalCan,
    icone: iconeOuPadrao(canal.desIconeCan),
    descricao: canal.desDescricaoCan?.trim() ?? '',
    contato: contatoDoCanal(canal.desTipoCan, canal.desContatoCan),
    blocos: semNulos(
      (canal.blocos ?? []).map((bloco) =>
        blocoSecao(
          bloco.desTituloBca,
          bloco.desConteudoBca,
          aberturaDoComportamento(bloco.desComportamentoBca),
          canal.desCanalCan
        )
      )
    ),
  }));
}

export function secoesDaPagina(pagina: ChavePagina, itens: readonly ItemPublico[]): SecaoAtendimento[] {
  switch (pagina) {
    case 'perguntas':
      return secoesDePerguntas(itens as PerguntaFrequentePublicaResponse[]);
    case 'politica':
      return secoesDePoliticas(itens as PoliticaAtendimentoPublicaResponse[]);
    default:
      return secoesDeCanais(itens as CanalAtendimentoPublicoResponse[]);
  }
}

/** O texto em que a busca procura: título, descrição, contato e título e texto dos blocos. */
export function textoDaSecao(secao: SecaoAtendimento): string {
  const contato = secao.contato ? `${secao.contato.rotulo} ${secao.contato.valor}` : '';
  const blocos = secao.blocos.map((bloco) => `${bloco.titulo} ${bloco.texto}`).join(' ');

  return `${secao.titulo} ${secao.descricao} ${contato} ${blocos}`;
}

/**
 * Um bloco de canal pelo slug, para a página do bloco: o primeiro par canal e bloco, na ordem
 * da API, cujos nomes dão os dois slugs. Nomes repetidos ficam com o primeiro, como serviço e
 * categoria.
 */
export function blocoDoCanal(
  secoes: readonly SecaoAtendimento[],
  slugCanal: string,
  slugBloco: string
): { canal: string; bloco: BlocoSecao } | null {
  for (const secao of secoes) {
    if (slugDeServico(secao.titulo) !== slugCanal) continue;

    const bloco = secao.blocos.find((item) => slugDeServico(item.titulo) === slugBloco);
    if (bloco) return { canal: secao.titulo, bloco };
  }

  return null;
}

/**
 * A imagem pela configuração do órgão. A enviada vai com a versão em `?v=`: a URL é fixa e tem
 * cache de 1 hora, e sem a versão a troca no adm não apareceria a quem já tinha a anterior.
 */
export function imagemDaPagina(pagina: ChavePagina, configuracao: PaginaPublicaResponse | null): ImagemAtendimento {
  const padrao = CONTEUDO_PAGINAS[pagina].ilustracao;

  if (configuracao && !configuracao.bitExibirImagemPgp) {
    return { src: null, largura: padrao.largura, altura: padrao.altura, oculta: true };
  }

  const versao = configuracao?.desVersaoImagemPgp?.trim();
  if (!versao) {
    return { src: padrao.src, largura: padrao.largura, altura: padrao.altura, oculta: false };
  }

  return {
    src: `${RECURSO}/pagina/${CHAVE_API[pagina]}/imagem?v=${encodeURIComponent(versao)}`,
    largura: IMAGEM_ENVIADA.largura,
    altura: IMAGEM_ENVIADA.altura,
    oculta: false,
  };
}

/** O valor do recurso, ou nulo em erro: `value()` lança quando o recurso está em erro. */
function valor<T>(recurso: HttpResourceRef<ResponseApi<T> | undefined>): T | null {
  if (recurso.error() !== undefined) return null;

  return recurso.value()?.data ?? null;
}

/**
 * As páginas Perguntas frequentes, Política de atendimento e Outros canais, lidas das rotas
 * públicas. O portal não envia o órgão: o back-end o resolve pelo host.
 *
 * Cada método cria um `httpResource` e precisa ser chamado num contexto de injeção (o
 * inicializador de campo do componente): o recurso pertence à tela, só busca quando ela abre e
 * pede só a página aberta.
 */
@Service()
export class PaginasAtendimentoPublicoService {
  /** As seções da página, refeitas quando a página muda. */
  conteudo(pagina: () => ChavePagina): ConteudoAtendimento {
    const recurso = httpResource<ResponseApi<ItemPublico[]>>(() => ({
      url: `${RECURSO}/${LISTA_API[pagina()]}`,
      context: ERRO_LOCAL(),
    }));

    return {
      secoes: computed(() => secoesDaPagina(pagina(), valor(recurso) ?? [])),
      carregando: computed(() => recurso.isLoading()),
      erro: computed(() => recurso.error() !== undefined),
      recarregar: () => recurso.reload(),
    };
  }

  /** Os canais publicados, para a página de um bloco. */
  canais(): ConteudoAtendimento {
    return this.conteudo(() => 'canais');
  }

  /** A imagem da página: carregando, nenhuma; com erro, a ilustração padrão. */
  imagem(pagina: () => ChavePagina): Signal<ImagemAtendimento> {
    const recurso = httpResource<ResponseApi<PaginaPublicaResponse>>(() => ({
      url: `${RECURSO}/pagina/${CHAVE_API[pagina()]}`,
      context: ERRO_LOCAL(),
    }));

    return computed(() => {
      const padrao = CONTEUDO_PAGINAS[pagina()].ilustracao;

      if (recurso.isLoading()) {
        return { src: null, largura: padrao.largura, altura: padrao.altura, oculta: false };
      }

      return imagemDaPagina(pagina(), valor(recurso));
    });
  }
}
